import React, { useState, useRef, useEffect, useCallback } from "react";

// ── Constants ──────────────────────────────────────────────────────────
const CONTAINER_SIZE = 380; // the dark background area (shows image context)
const VIEWPORT_SIZE = 300; // the crop circle inside the container
const INSET = (CONTAINER_SIZE - VIEWPORT_SIZE) / 2; // 30px margin of visible context
const MIN_ZOOM = 1;
const MAX_ZOOM = 5;
const ZOOM_STEP = 0.01;

// ── Component ──────────────────────────────────────────────────────────

function ImageCropper({
  imageDataUrl,
  onCrop,
  onCancel,
  shape = "circle",
  outputSize = 300,
}) {
  const containerRef = useRef(null);
  const imgRef = useRef(null);

  const [naturalSize, setNaturalSize] = useState(null);
  const [zoom, setZoom] = useState(1);
  const [panX, setPanX] = useState(0);
  const [panY, setPanY] = useState(0);
  const [drag, setDrag] = useState(null);

  // ── Derived values ──────────────────────────────────────────────────

  // Circle crop: shorter side fills the viewport (zoom into face)
  // Square crop: longest side fits the viewport (see the whole image)
  const baseScale = naturalSize
    ? shape === "circle"
      ? VIEWPORT_SIZE / Math.min(naturalSize.w, naturalSize.h)
      : VIEWPORT_SIZE / Math.max(naturalSize.w, naturalSize.h)
    : 1;
  const currentScale = baseScale * zoom;

  // Clamp pan so the image always fully covers the crop circle (no empty space)
  const clampPan = useCallback(
    (px, py, z) => {
      if (!naturalSize) return { x: 0, y: 0 };
      const s = baseScale * z;
      const mX = Math.max(0, (naturalSize.w * s - VIEWPORT_SIZE) / 2);
      const mY = Math.max(0, (naturalSize.h * s - VIEWPORT_SIZE) / 2);
      return {
        x: Math.max(-mX, Math.min(mX, px)),
        y: Math.max(-mY, Math.min(mY, py)),
      };
    },
    [naturalSize, baseScale]
  );

  // ── Image load ──────────────────────────────────────────────────────

  const handleImageLoad = useCallback(() => {
    const img = imgRef.current;
    if (!img) return;
    setNaturalSize({ w: img.naturalWidth, h: img.naturalHeight });
    setZoom(1);
    setPanX(0);
    setPanY(0);
  }, []);

  // ── Zoom change ─────────────────────────────────────────────────────

  const handleZoomChange = useCallback(
    (newZoom) => {
      const z = Math.max(MIN_ZOOM, Math.min(MAX_ZOOM, Number(newZoom)));
      setZoom(z);
      const clamped = clampPan(panX, panY, z);
      setPanX(clamped.x);
      setPanY(clamped.y);
    },
    [panX, panY, clampPan]
  );

  // ── Mouse / touch drag to pan ───────────────────────────────────────

  const handlePointerDown = useCallback(
    (e) => {
      e.preventDefault();
      const clientX = e.touches ? e.touches[0].clientX : e.clientX;
      const clientY = e.touches ? e.touches[0].clientY : e.clientY;
      setDrag({ startX: clientX, startY: clientY, startPanX: panX, startPanY: panY });
    },
    [panX, panY]
  );

  useEffect(() => {
    if (!drag) return;

    const handleMove = (e) => {
      const clientX = e.touches ? e.touches[0].clientX : e.clientX;
      const clientY = e.touches ? e.touches[0].clientY : e.clientY;
      const dx = clientX - drag.startX;
      const dy = clientY - drag.startY;
      const clamped = clampPan(drag.startPanX + dx, drag.startPanY + dy, zoom);
      setPanX(clamped.x);
      setPanY(clamped.y);
    };

    const handleUp = () => setDrag(null);

    window.addEventListener("mousemove", handleMove);
    window.addEventListener("mouseup", handleUp);
    window.addEventListener("touchmove", handleMove, { passive: false });
    window.addEventListener("touchend", handleUp);
    return () => {
      window.removeEventListener("mousemove", handleMove);
      window.removeEventListener("mouseup", handleUp);
      window.removeEventListener("touchmove", handleMove);
      window.removeEventListener("touchend", handleUp);
    };
  }, [drag, zoom, clampPan]);

  // ── Export (single canvas pass — crop + resize together) ────────────

  const handleCrop = useCallback(() => {
    if (!naturalSize) return;

    const img = imgRef.current;
    const srcSize = VIEWPORT_SIZE / currentScale;
    const centerX = naturalSize.w / 2 - panX / currentScale;
    const centerY = naturalSize.h / 2 - panY / currentScale;

    const canvas = document.createElement("canvas");
    canvas.width = outputSize;
    canvas.height = outputSize;
    const ctx = canvas.getContext("2d");

    if (shape === "circle") {
      ctx.beginPath();
      ctx.arc(outputSize / 2, outputSize / 2, outputSize / 2, 0, Math.PI * 2);
      ctx.closePath();
      ctx.clip();
    }

    ctx.drawImage(
      img,
      centerX - srcSize / 2,
      centerY - srcSize / 2,
      srcSize,
      srcSize,
      0,
      0,
      outputSize,
      outputSize
    );

    onCrop(canvas.toDataURL("image/jpeg", 0.85));
  }, [naturalSize, currentScale, panX, panY, outputSize, shape, onCrop]);

  // ── Render ──────────────────────────────────────────────────────────

  if (!imageDataUrl) return null;

  const borderRadius = shape === "circle" ? "50%" : "12px";

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-[16px]">
      <div className="bg-white rounded-[16px] shadow-2xl max-w-[560px] w-full flex flex-col items-center p-[24px] gap-y-[16px]">
        <div className="text-[20px] font-display font-bold text-dupontBlue">
          {shape === "circle" ? "Crop Your Photo" : "Crop Your Close Up"}
        </div>
        <div className="text-[15px] text-dupontBlue/70">
          {shape === "circle"
            ? "Drag to reposition your face in the circle. Use the slider to zoom in or out."
            : "Position your photo in the frame. This is what cousins see when they click your profile."}
        </div>

        {/* Container — shows image context around the crop area */}
        <div
          ref={containerRef}
          style={{
            width: CONTAINER_SIZE,
            height: CONTAINER_SIZE,
            background: "#111",
            borderRadius: 16,
            overflow: "hidden",
            position: "relative",
            cursor: drag ? "grabbing" : "grab",
            touchAction: "none",
          }}
          onMouseDown={handlePointerDown}
          onTouchStart={handlePointerDown}
        >
          {/* The image — visible everywhere, dimmed outside the crop circle */}
          <img
            ref={imgRef}
            src={imageDataUrl}
            alt="Crop preview"
            onLoad={handleImageLoad}
            draggable={false}
            style={{
              position: "absolute",
              left: CONTAINER_SIZE / 2,
              top: CONTAINER_SIZE / 2,
              transform: `translate(-50%, -50%) translate(${panX}px, ${panY}px)`,
              width: naturalSize ? naturalSize.w * currentScale : "auto",
              height: naturalSize ? naturalSize.h * currentScale : "auto",
              pointerEvents: "none",
              userSelect: "none",
            }}
          />

          {/* Crop circle overlay — dims everything outside, gold border on the circle */}
          <div
            style={{
              position: "absolute",
              left: INSET,
              top: INSET,
              width: VIEWPORT_SIZE,
              height: VIEWPORT_SIZE,
              borderRadius,
              border: "2px solid #b5a03e",
              boxShadow: "0 0 0 9999px rgba(0,0,0,0.55)",
              pointerEvents: "none",
            }}
          />
        </div>

        {/* Zoom slider */}
        <div className="flex items-center gap-x-[12px] w-full max-w-[300px]">
          <span className="text-[18px] text-dupontBlue/50">&minus;</span>
          <input
            type="range"
            min={MIN_ZOOM}
            max={MAX_ZOOM}
            step={ZOOM_STEP}
            value={zoom}
            onChange={(e) => handleZoomChange(e.target.value)}
            className="flex-1 accent-dupontGold"
          />
          <span className="text-[18px] text-dupontBlue/50">+</span>
        </div>

        {/* Buttons */}
        <div className="flex gap-x-[12px] mt-[8px]">
          <div
            onClick={onCancel}
            className="inline-block rounded-full px-[16px] py-[12px] w-fit h-fit text-center bg-dupontBlue cursor-pointer"
          >
            <div className="text-sm font-medium whitespace-nowrap text-white">
              Cancel
            </div>
          </div>
          <div
            onClick={handleCrop}
            className="inline-block rounded-full px-[16px] py-[12px] w-fit h-fit text-center bg-dupontGold cursor-pointer"
          >
            <div className="text-sm font-medium whitespace-nowrap text-white">
              Crop &amp; Use
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ImageCropper;
